/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useEffect } from 'react';
import { Employee, EmployeeType, OrganizationalStructure, HRMetrics, OrganizationalCosts } from '../../types';
import { EMPLOYEE_CONFIG, DEPARTMENT_BUDGET_ALLOCATION, ORGANIZATIONAL_BENCHMARKS } from '../../constants/orgManagement';
import { orgManagementService } from '../../services/orgManagementService';
import { resolveHireEmployee } from '../../src/adapters/ui/hooks/useOrgMutations';
import { EnterpriseDashboardShell } from './kit';

interface HRDashboardEnhancedProps {
  onClose: () => void;
  budget: number;
}

const HRDashboardEnhanced: React.FC<HRDashboardEnhancedProps> = ({ onClose, budget }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [orgStructure, setOrgStructure] = useState<OrganizationalStructure | null>(null);
  const [hrMetrics, setHrMetrics] = useState<HRMetrics | null>(null);
  const [orgCosts, setOrgCosts] = useState<OrganizationalCosts | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'headcount' | 'hiring' | 'metrics' | 'costs' | 'analytics'>('overview');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [timeRange, setTimeRange] = useState<'current' | 'projected'>('current');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setEmployees(orgManagementService.getEmployees());
    setOrgStructure(orgManagementService.calculateOrganizationalStructure());
    setHrMetrics(orgManagementService.calculateHRMetrics());
    setOrgCosts(orgManagementService.calculateOrganizationalCosts());
  };

  const handleHireEmployee = (type: EmployeeType) => {
    const result = resolveHireEmployee({ type, currentOrgSize: employees.length });
    if (result.ok && result.employee) {
      console.log('Hired employee:', result.employee);
      loadData();
      showNotification('Employee hired successfully!', 'success');
      return;
    }
    console.error('Error hiring employee:', result.error);
    showNotification('Failed to hire employee. Check budget requirements.', 'error');
  };

  const handleTerminateEmployee = (employeeId: string) => {
    try {
      // In a real implementation, this would remove the employee
      setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
      loadData();
      showNotification('Employee terminated successfully', 'warning');
    } catch (error) {
      console.error('Error terminating employee:', error);
      showNotification('Failed to terminate employee', 'error');
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    // Simple notification system - in a real app, you'd use a toast library
    console.log(`${type.toUpperCase()}: ${message}`);
  };

  const getDepartmentColor = (department: string): string => {
    const colors: Record<string, string> = {
      'Engineering': 'bg-gradient-to-r from-blue-500 to-blue-600',
      'Product': 'bg-gradient-to-r from-purple-500 to-purple-600',
      'Sales & Marketing': 'bg-gradient-to-r from-green-500 to-green-600',
      'Operations': 'bg-gradient-to-r from-orange-500 to-orange-600',
      'Compliance & Risk': 'bg-gradient-to-r from-red-500 to-red-600',
      'Finance': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
      'Human Resources': 'bg-gradient-to-r from-pink-500 to-pink-600',
      'Legal': 'bg-gradient-to-r from-indigo-500 to-indigo-600',
      'Executive': 'bg-gradient-to-r from-gray-600 to-gray-700'
    };
    return colors[department] || 'bg-gradient-to-r from-gray-500 to-gray-600';
  };

  const getPerformanceColor = (score: number): string => {
    if (score >= 90) return 'text-emerald-400 bg-emerald-900/20';
    if (score >= 75) return 'text-blue-400 bg-blue-900/20';
    if (score >= 60) return 'text-yellow-400 bg-yellow-900/20';
    return 'text-red-400 bg-red-900/20';
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getOrgSizeStage = (): string => {
    const size = employees.length;
    if (size <= 50) return 'startup';
    if (size <= 200) return 'growth';
    return 'scale';
  };

  const calculateEfficiencyScore = (): number => {
    if (!orgStructure || !hrMetrics) return 0;

    const avgProductivity = hrMetrics.performanceMetrics.avgProductivity;
    const satisfaction = hrMetrics.employeeSatisfaction;
    const turnoverOptimal = Math.max(0, 15 - hrMetrics.turnoverRate * 100); // Optimal is 10-15%

    return Math.round(((avgProductivity * 0.4) + (satisfaction * 0.3) + (turnoverOptimal * 0.3)) * 100);
  };

  const getBenchmarks = () => {
    const stage = getOrgSizeStage();
    return ORGANIZATIONAL_BENCHMARKS[stage];
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">👥</span>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${employees.length > 0 ? 'bg-green-900/50 text-green-400 border border-green-600/30' : 'bg-gray-900/50 text-gray-400 border border-gray-600/30'}`}>
              {employees.length > 0 ? 'Active' : 'Empty'}
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{employees.length}</div>
          <div className="text-sm text-slate-400 mb-1">Total Employees</div>
          <div className="text-xs text-slate-500">
            {getOrgSizeStage() === 'startup' && 'Startup Stage (0-50)'}
            {getOrgSizeStage() === 'growth' && 'Growth Stage (51-200)'}
            {getOrgSizeStage() === 'scale' && 'Scale Stage (200+)'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">😊</span>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${getPerformanceColor(hrMetrics?.employeeSatisfaction || 0)}`}>
              {hrMetrics ? `${hrMetrics.employeeSatisfaction}%` : 'N/A'}
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{hrMetrics?.employeeSatisfaction || 0}%</div>
          <div className="text-sm text-slate-400 mb-1">Employee Morale</div>
          <div className="text-xs text-slate-500">
            {hrMetrics?.employeeSatisfaction >= 80 ? 'Excellent' : hrMetrics?.employeeSatisfaction >= 60 ? 'Good' : 'Needs Improvement'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📈</span>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${getPerformanceColor((hrMetrics?.performanceMetrics.avgProductivity || 1) * 100)}`}>
              {hrMetrics ? `${Math.round((hrMetrics.performanceMetrics.avgProductivity || 1) * 100)}%` : 'N/A'}
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {hrMetrics ? `${Math.round((hrMetrics.performanceMetrics.avgProductivity || 1) * 100)}%` : 'N/A'}
          </div>
          <div className="text-sm text-slate-400 mb-1">Productivity Score</div>
          <div className="text-xs text-slate-500">
            {hrMetrics?.performanceMetrics.avgProductivity >= 1.2 ? 'High Performance' : 'Standard'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-orange-900/50 text-orange-400 border border-orange-600/30">
              Monthly
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{formatCurrency((orgCosts?.total || 0) / 12)}</div>
          <div className="text-sm text-slate-400 mb-1">Monthly Burn Rate</div>
          <div className="text-xs text-slate-500">
            Total: {formatCurrency(orgCosts?.total || 0)} annually
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">⚡</span>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${getPerformanceColor(calculateEfficiencyScore())}`}>
              {calculateEfficiencyScore()}%
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">{calculateEfficiencyScore()}%</div>
          <div className="text-sm text-slate-400 mb-1">Efficiency Score</div>
          <div className="text-xs text-slate-500">
            Combined performance metric
          </div>
        </div>
      </div>

      {/* Department Overview with Enhanced Visuals */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <span>Department Overview</span>
            <span className="text-sm font-normal text-slate-400">({Object.keys(orgStructure?.departments || {}).length} departments)</span>
          </h3>
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <span>📊</span>
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
        </div>

        {orgStructure && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(orgStructure.departments).map(([dept, data]) => {
              const benchmark = getBenchmarks();
              const isAboveBenchmark = data.headcount > (employees.length * benchmark[dept.replace('Sales & Marketing', 'sales').replace('Compliance & Risk', 'compliance').toLowerCase()]);

              return (
                <div key={dept} className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-xl" style={{ background: getDepartmentColor(dept) }}></div>
                  <div className="relative bg-slate-900/50 backdrop-blur-sm rounded-xl p-5 border border-slate-600 group-hover:border-slate-500 transition-all duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full ${getDepartmentColor(dept)}`}></div>
                        <div>
                          <div className="text-white font-semibold text-lg">{dept}</div>
                          <div className="text-xs text-slate-400">{data.headcount} team members</div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <div className="text-white font-bold text-lg">{formatCurrency(data.avgSalary)}</div>
                        <div className="text-xs text-slate-400">avg salary</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="text-center">
                        <div className="text-sm text-slate-400">Experience</div>
                        <div className="text-white font-medium">{data.avgExperience}y</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-slate-400">Productivity</div>
                        <div className="text-white font-medium">{Math.round(data.productivity * 100)}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-slate-400">Morale</div>
                        <div className="text-white font-medium">{data.morale}%</div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="text-sm text-slate-400">
                        Budget Allocation: <span className="text-white font-medium">{Math.round(data.budgetAllocation / (orgCosts?.total || 1) * 100)}%</span>
                      </div>
                      {isAboveBenchmark && (
                        <span className="px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full border border-green-600/30">
                          Above Benchmark
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Analytics Section */}
        {showAnalytics && (
          <div className="mt-6 pt-6 border-t border-slate-700">
            <h4 className="text-lg font-semibold text-white mb-4">Departmental Analytics</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <h5 className="text-white font-medium mb-3">Cost per Employee</h5>
                <div className="space-y-2">
                  {orgStructure && Object.entries(orgStructure.departments).map(([dept, data]) => (
                    <div key={dept} className="flex justify-between items-center">
                      <span className="text-slate-300">{dept}</span>
                      <span className="text-white font-medium">
                        {formatCurrency((data.budgetAllocation || 0) / data.headcount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <h5 className="text-white font-medium mb-3">Efficiency Comparison</h5>
                <div className="space-y-2">
                  {orgStructure && Object.entries(orgStructure.departments).slice(0, 3).map(([dept, data]) => (
                    <div key={dept} className="flex justify-between items-center">
                      <span className="text-slate-300">{dept}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-700 rounded-full h-2">
                          <div
                            className="h-2 bg-gradient-to-r from-red-500 to-yellow-500 to-green-500 rounded-full"
                            style={{ width: `${(data.productivity / 1.5) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-white text-sm font-medium">
                          {Math.round((data.productivity / 1.5) * 100)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Critical Insights */}
        <div className="mt-6 pt-6 border-t border-slate-700">
          <h4 className="text-lg font-semibold text-white mb-4">Critical Insights & Recommendations</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.length === 0 && (
              <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4">
                <h5 className="text-yellow-400 font-medium mb-2">🚀 Get Started</h5>
                <p className="text-slate-300 text-sm">Hire your first employees to build your fintech team. Start with essential roles like CEO, CTO, and Compliance Officer.</p>
              </div>
            )}

            {hrMetrics && hrMetrics.turnoverRate > 0.20 && (
              <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-4">
                <h5 className="text-red-400 font-medium mb-2">⚠️ High Turnover Alert</h5>
                <p className="text-slate-300 text-sm">Turnover rate is {Math.round(hrMetrics.turnoverRate * 100)}%. Consider reviewing compensation and work conditions.</p>
              </div>
            )}

            {hrMetrics && hrMetrics.employeeSatisfaction < 60 && (
              <div className="bg-orange-900/20 border border-orange-800/30 rounded-lg p-4">
                <h5 className="text-orange-400 font-medium mb-2">📉 Low Morale</h5>
                <p className="text-slate-300 text-sm">Employee satisfaction is {hrMetrics.employeeSatisfaction}%. Consider implementing engagement programs.</p>
              </div>
            )}

            {employees.length > 0 && employees.length < 10 && (
              <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4">
                <h5 className="text-blue-400 font-medium mb-2">💡 Growing Team</h5>
                <p className="text-slate-300 text-sm">Focus on hiring key specialists to expand capabilities and meet compliance requirements.</p>
              </div>
            )}

            {orgCosts && orgCosts.benefits / orgCosts.salaries > 0.35 && (
              <div className="bg-purple-900/20 border border-purple-800/30 rounded-lg p-4">
                <h5 className="text-purple-400 font-medium mb-2">💰 Benefits Optimization</h5>
                <p className="text-slate-300 text-sm">Benefits are {Math.round((orgCosts.benefits / orgCosts.salaries) * 100)}% of salaries. Review packages for cost efficiency.</p>
              </div>
            )}

            {hrMetrics && hrMetrics.performanceMetrics.highPerformers < 20 && (
              <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-4">
                <h5 className="text-green-400 font-medium mb-2">🌟 Performance Opportunity</h5>
                <p className="text-slate-300 text-sm">Only {hrMetrics.performanceMetrics.highPerformers}% high performers. Consider performance improvement programs.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderHeadcount = () => (
    <div className="space-y-6">
      {/* Enhanced Filters and Controls */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4">
            <select
              className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              <option value="all">All Departments</option>
              {orgStructure && Object.keys(orgStructure.departments).map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                {viewMode === 'grid' ? '📋' : '⊞'}
              </button>
              <span className="text-sm text-slate-400">{viewMode === 'grid' ? 'Grid View' : 'List View'}</span>
            </div>
          </div>

          <div className="flex gap-4 items-center">
            <div className="text-sm text-slate-400">
              <span className="font-medium">{employees.length}</span> employees
            </div>
            <div className="text-sm text-slate-400">
              <span className="font-medium">{orgStructure ? Object.keys(orgStructure.departments).length : 0}</span> departments
            </div>
          </div>
        </div>
      </div>

      {/* Employee Cards/List */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">Employee Directory</h3>
          <div className="text-sm text-slate-400">
            Showing {employees.filter(emp => selectedDepartment === 'all' || emp.department === selectedDepartment).length} employees
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees
              .filter(emp => selectedDepartment === 'all' || emp.department === selectedDepartment)
              .map(employee => {
                const config = EMPLOYEE_CONFIG[employee.type];
                const performanceColor = employee.productivity >= 1.2 ? 'green' : employee.productivity >= 1.0 ? 'blue' : 'orange';
                const moraleColor = employee.morale >= 80 ? 'green' : employee.morale >= 60 ? 'blue' : 'red';

                return (
                  <div key={employee.id} className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-xl"></div>
                    <div className="relative bg-slate-900/50 backdrop-blur-sm rounded-xl p-5 border border-slate-600 group-hover:border-slate-500 transition-all duration-300">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 ${getDepartmentColor(employee.department)} rounded-lg flex items-center justify-center`}>
                            <span className="text-white text-sm font-bold">
                              {employee.type.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <div className="text-white font-medium">{employee.name}</div>
                            <div className="text-sm text-slate-400">{config?.name}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedEmployee(employee)}
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          Details
                        </button>
                      </div>

                      {/* Key Metrics */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="text-center">
                          <div className="text-xs text-slate-400 mb-1">Experience</div>
                          <div className="text-white font-medium">{employee.experience} yrs</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-slate-400 mb-1">Level</div>
                          <div className="text-white font-medium">{config?.level}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-slate-400 mb-1">Training</div>
                          <div className="text-white font-medium">Lv {employee.trainingLevel}</div>
                        </div>
                      </div>

                      {/* Performance Bars */}
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs text-slate-400 mb-1">
                            <span>Productivity</span>
                            <span>{Math.round(employee.productivity * 100)}%</span>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                performanceColor === 'green' ? 'bg-green-500' :
                                performanceColor === 'blue' ? 'bg-blue-500' :
                                'bg-orange-500'
                              }`}
                              style={{ width: `${(employee.productivity / 1.5) * 100}%` }}
                            ></div>
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between text-xs text-slate-400 mb-1">
                            <span>Morale</span>
                            <span>{employee.morale}%</span>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                moraleColor === 'green' ? 'bg-green-500' :
                                moraleColor === 'blue' ? 'bg-blue-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${employee.morale}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Compensation */}
                      <div className="pt-4 border-t border-slate-700">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-400">Salary</span>
                          <span className="text-white font-medium">{formatCurrency(employee.salary)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-slate-400">Total Comp</span>
                          <span className="text-green-400 font-medium">{formatCurrency(employee.totalCompensation)}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="pt-4 border-t border-slate-700 flex justify-between items-center">
                        <div className="flex gap-2">
                          {employee.certifications.map(cert => (
                            <span key={cert} className="px-2 py-1 bg-blue-900/30 text-blue-400 text-xs rounded border border-blue-700/50">
                              {cert}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={() => handleTerminateEmployee(employee.id)}
                          className="text-red-400 hover:text-red-300 text-xs"
                        >
                          Terminate
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-600">
                  <th className="text-left py-3 px-4 text-slate-300">Name</th>
                  <th className="text-left py-3 px-4 text-slate-300">Role</th>
                  <th className="text-left py-3 px-4 text-slate-300">Department</th>
                  <th className="text-left py-3 px-4 text-slate-300">Experience</th>
                  <th className="text-left py-3 px-4 text-slate-300">Salary</th>
                  <th className="text-left py-3 px-4 text-slate-300">Productivity</th>
                  <th className="text-left py-3 px-4 text-slate-300">Morale</th>
                  <th className="text-left py-3 px-4 text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees
                  .filter(emp => selectedDepartment === 'all' || emp.department === selectedDepartment)
                  .map(employee => (
                    <tr key={employee.id} className="border-b border-slate-700 hover:bg-slate-700/50 transition-colors">
                      <td className="py-3 px-4">
                        <div>
                          <div className="text-white font-medium">{employee.name}</div>
                          <div className="text-xs text-slate-400">{EMPLOYEE_CONFIG[employee.type]?.name}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-300">{employee.department}</td>
                      <td className="py-3 px-4 text-slate-300">{employee.experience} yrs</td>
                      <td className="py-3 px-4 text-white">{formatCurrency(employee.salary)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-slate-700 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-blue-500"
                              style={{ width: `${(employee.productivity / 1.5) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-white text-xs">{Math.round(employee.productivity * 100)}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-slate-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                employee.morale >= 80 ? 'bg-green-500' :
                                employee.morale >= 60 ? 'bg-blue-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${employee.morale}%` }}
                            ></div>
                          </div>
                          <span className="text-white text-xs">{employee.morale}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => setSelectedEmployee(employee)}
                          className="text-blue-400 hover:text-blue-300 mr-2"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => handleTerminateEmployee(employee.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Department Statistics */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-4">Department Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {orgStructure && Object.entries(orgStructure.departments).map(([dept, data]) => (
            <div key={dept} className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium">{dept}</h4>
                <div className={`w-3 h-3 rounded-full ${getDepartmentColor(dept)}`}></div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Team Size</span>
                  <span className="text-white font-medium">{data.headcount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Avg Salary</span>
                  <span className="text-white font-medium">{formatCurrency(data.avgSalary)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Budget</span>
                  <span className="text-white font-medium">{Math.round(data.budgetAllocation / (orgCosts?.total || 1) * 100)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderHiring = () => (
    <div className="space-y-6">
      {/* Hiring Pipeline Analytics */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-6">Hiring Pipeline Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">📝</span>
            </div>
            <div className="text-2xl font-bold text-white">15</div>
            <div className="text-sm text-slate-400">Applications</div>
            <div className="text-xs text-green-400">+3 this week</div>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">🔍</span>
            </div>
            <div className="text-2xl font-bold text-white">8</div>
            <div className="text-sm text-slate-400">Screening</div>
            <div className="text-xs text-yellow-400">In progress</div>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">🗣️</span>
            </div>
            <div className="text-2xl font-bold text-white">5</div>
            <div className="text-sm text-slate-400">Interviews</div>
            <div className="text-xs text-orange-400">2 scheduled</div>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">✅</span>
            </div>
            <div className="text-2xl font-bold text-white">3</div>
            <div className="text-sm text-slate-400">Offers</div>
            <div className="text-xs text-green-400">1 accepted</div>
          </div>
        </div>

        {/* Hiring Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
            <h4 className="text-white font-medium mb-3">Time to Hire</h4>
            <div className="text-3xl font-bold text-white mb-2">42 days</div>
            <div className="text-sm text-slate-400">Average time from application to offer</div>
            <div className="mt-2 flex items-center text-xs text-yellow-400">
              <span className="mr-1">⬆</span>
              <span>8% faster than last month</span>
            </div>
          </div>

          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
            <h4 className="text-white font-medium mb-3">Acceptance Rate</h4>
            <div className="text-3xl font-bold text-white mb-2">60%</div>
            <div className="text-sm text-slate-400">Offer acceptance rate</div>
            <div className="mt-2 flex items-center text-xs text-green-400">
              <span className="mr-1">⬆</span>
              <span>5% improvement</span>
            </div>
          </div>

          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
            <h4 className="text-white font-medium mb-3">Cost per Hire</h4>
            <div className="text-3xl font-bold text-white mb-2">{formatCurrency(8500)}</div>
            <div className="text-sm text-slate-400">Total recruitment cost per hire</div>
            <div className="mt-2 flex items-center text-xs text-blue-400">
              <span className="mr-1">⬇</span>
              <span>Within industry standard</span>
            </div>
          </div>
        </div>
      </div>

      {/* Open Positions */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <span>Open Positions</span>
            <span className="text-sm font-normal text-slate-400">({Object.values(EMPLOYEE_CONFIG).slice(0, 12).length} available roles)</span>
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setTimeRange('current')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeRange === 'current'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Current
            </button>
            <button
              onClick={() => setTimeRange('projected')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeRange === 'projected'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Projected
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(EMPLOYEE_CONFIG).slice(0, 12).map(([type, config]) => {
            const currentOpenings = type === EmployeeType.SeniorEngineer ? 3 : type === EmployeeType.Engineer ? 5 : type === EmployeeType.ComplianceAnalyst ? 2 : 1;
            const canAfford = budget >= config.baseSalary * 0.1;
            const marketHotness = config.marketDemand === 'Very High' || config.marketDemand === 'High';

            return (
              <div key={type} className={`relative group transition-all duration-300 ${
                canAfford
                  ? 'hover:scale-105 hover:shadow-xl'
                  : 'opacity-60 cursor-not-allowed'
              }`}>
                <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-xl"></div>
                <div className={`relative border rounded-xl p-5 transition-all duration-300 ${
                  canAfford
                    ? 'border-slate-600 bg-slate-800 hover:border-blue-500'
                    : 'border-red-900/50 bg-red-900/10'
                }`}>
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h4 className="text-white font-bold text-lg mb-1">{config.name}</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-400">{config.department}</span>
                        <span className="text-sm text-slate-400">Level {config.level}</span>
                        {marketHotness && (
                          <span className="px-2 py-1 bg-red-900/50 text-red-400 text-xs rounded-full border border-red-600/30">
                            {config.marketDemand}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-400 mb-1">Market Demand</div>
                      <div className={`text-sm font-medium ${
                        config.marketDemand === 'Very High' ? 'text-red-400' :
                        config.marketDemand === 'High' ? 'text-orange-400' :
                        'text-blue-400'
                      }`}>
                        {config.marketDemand}
                      </div>
                    </div>
                  </div>

                  {/* Compensation */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-slate-400 mb-1">Base Salary</div>
                      <div className="text-white font-bold">{formatCurrency(config.baseSalary)}</div>
                      <div className="text-xs text-slate-500">{formatCurrency(config.baseSalary * 0.08)} /month</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">Total Comp</div>
                      <div className="text-green-400 font-bold">{formatCurrency(config.totalCompRange[1])}</div>
                      <div className="text-xs text-slate-500">incl. equity & bonus</div>
                    </div>
                  </div>

                  {/* Skills & Requirements */}
                  <div className="mb-4">
                    <div className="text-xs text-slate-400 mb-2">Key Skills</div>
                    <div className="flex flex-wrap gap-1">
                      {config.requiredSkills.slice(0, 3).map(skill => (
                        <span key={skill} className="px-2 py-1 bg-slate-700 text-blue-300 text-xs rounded">
                          {skill}
                        </span>
                      ))}
                      {config.requiredSkills.length > 3 && (
                        <span className="px-2 py-1 bg-slate-700 text-slate-400 text-xs rounded">
                          +{config.requiredSkills.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Business Impact */}
                  <div className="mb-4">
                    <div className="text-xs text-slate-400 mb-2">Business Impact</div>
                    <div className="text-sm text-slate-300">
                      {config.responsibilities[0]} • {config.responsibilities[1]}
                    </div>
                  </div>

                  {/* Hiring Status */}
                  <div className="flex justify-between items-center">
                    <div className="text-sm">
                      <span className="text-slate-400">Openings: </span>
                      <span className="text-white font-medium">{currentOpenings}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-xs text-slate-400">
                        {currentOpenings > 0 && `${currentOpenings} candidates`}
                      </span>
                      {canAfford ? (
                        <button
                          onClick={() => handleHireEmployee(type as EmployeeType)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          Hire
                        </button>
                      ) : (
                        <div className="px-3 py-1 bg-slate-700 text-slate-500 rounded-lg text-sm font-medium cursor-not-allowed">
                          Insufficient Budget
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Success Metrics */}
                  <div className="border-t border-slate-700 pt-3 mt-3">
                <div className="text-xs text-slate-400 mb-2">Success Metrics</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400">Time to Productivity:</span>
                    <span className="text-green-400 font-medium">3 months</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Retention Rate:</span>
                    <span className="text-blue-400 font-medium">85%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Continue with other render methods...
  const renderMetrics = () => (
    <div className="space-y-6">
      {/* Advanced HR Analytics Dashboard */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-6">Advanced HR Analytics</h3>

        {hrMetrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Performance Metrics */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">Performance Metrics</h4>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-400">{hrMetrics.performanceMetrics.highPerformers}%</div>
                    <div className="text-sm text-slate-400">High Performers</div>
                    <div className="text-xs text-green-400 mt-1">
                      {hrMetrics.performanceMetrics.highPerformers > 25 ? 'Excellent' : 'Good'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-400">{hrMetrics.performanceMetrics.improvementNeeded}%</div>
                    <div className="text-sm text-slate-400">Need Improvement</div>
                    <div className="text-xs text-orange-400 mt-1">
                      {hrMetrics.performanceMetrics.improvementNeeded < 15 ? 'Good' : 'Review Required'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-400">{Math.round(hrMetrics.performanceMetrics.avgProductivity * 100)}%</div>
                    <div className="text-sm text-slate-400">Avg Productivity</div>
                    <div className="text-xs text-blue-400 mt-1">
                      {hrMetrics.performanceMetrics.avgProductivity >= 1.1 ? 'Above Average' : 'At Target'}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700">
                  <h5 className="text-white font-medium mb-3">Performance Distribution</h5>
                  <div className="space-y-2">
                    {[
                      { label: 'Exceeds Expectations (120%+)', percentage: 15, color: 'bg-green-600' },
                      { label: 'Meets Expectations (100-119%)', percentage: 35, color: 'bg-blue-600' },
                      { label: 'Needs Improvement (80-99%)', percentage: 35, color: 'bg-yellow-600' },
                      { label: 'Below Expectations (<80%)', percentage: 15, color: 'bg-red-600' }
                    ].map((segment, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="text-sm text-slate-300 w-48">{segment.label}</div>
                        <div className="flex-1 bg-slate-700 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full ${segment.color} transition-all duration-500`}
                            style={{ width: `${segment.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-white font-medium w-12 text-right">{segment.percentage}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recruitment Metrics */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white mb-4">Recruitment Metrics</h4>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-2xl font-bold text-orange-400">{Math.round(hrMetrics.turnoverRate * 100)}%</div>
                      <div className="text-sm text-slate-400">Annual Turnover Rate</div>
                      <div className="text-xs text-orange-400 mt-1">
                        {hrMetrics.turnoverRate <= 0.15 ? 'Excellent' : hrMetrics.turnoverRate <= 0.25 ? 'Good' : 'High - Review Required'}
                      </div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-400">{hrMetrics.avgTimeToFill} days</div>
                      <div className="text-sm text-slate-400">Avg Time to Fill</div>
                      <div className="text-xs text-blue-400 mt-1">
                        {hrMetrics.avgTimeToFill <= 30 ? 'Excellent' : hrMetrics.avgTimeToFill <= 45 ? 'Good' : 'Slow - Optimize'}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <div className="text-lg font-bold text-purple-400">{hrMetrics.trainingHoursPerEmployee} hrs</div>
                      <div className="text-sm text-slate-400">Training per Employee</div>
                      <div className="text-xs text-purple-400 mt-1">
                        {hrMetrics.trainingHoursPerEmployee >= 40 ? 'Compliant' : 'Below Standard'}
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-pink-400">{hrMetrics.employeeSatisfaction}%</div>
                      <div className="text-sm text-slate-400">Employee Satisfaction</div>
                      <div className="text-xs text-pink-400 mt-1">
                        {hrMetrics.employeeSatisfaction >= 80 ? 'Excellent' : hrMetrics.employeeSatisfaction >= 70 ? 'Good' : 'Needs Attention'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Diversity & Inclusion */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">Diversity & Inclusion</h4>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-400">{hrMetrics.diversityMetrics.gender}%</div>
                    <div className="text-sm text-slate-400">Female Representation</div>
                    <div className="text-xs text-purple-400 mt-1">
                      {hrMetrics.diversityMetrics.gender >= 40 ? 'Industry Leading' : 'Above Average'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">{hrMetrics.diversityMetrics.ethnicity}%</div>
                    <div className="text-sm text-slate-400">Underrepresented Minorities</div>
                    <div className="text-xs text-blue-400 mt-1">
                      {hrMetrics.diversityMetrics.ethnicity >= 30 ? 'Good Progress' : 'Room for Improvement'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">{hrMetrics.diversityMetrics.age} yrs</div>
                    <div className="text-sm text-slate-400">Average Age</div>
                    <div className="text-xs text-green-400 mt-1">
                      Balanced Age Distribution
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700">
                  <h5 className="text-white font-medium mb-3">DEI Initiatives Progress</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-300">Unconscious Bias Training</span>
                      <div className="text-xs text-green-400">Complete</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-300">Leadership Development</span>
                      <div className="text-xs text-yellow-400">In Progress</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-300">Mentorship Program</span>
                      <div className="text-xs text-blue-400">75% Complete</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderCosts = () => (
    <div className="space-y-6">
      {/* Cost Analysis Dashboard */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
        <h3 className="text-xl font-bold text-white mb-6">Cost Analysis & Budgeting</h3>

        {orgCosts && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cost Breakdown */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">Cost Breakdown</h4>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                <div className="space-y-3">
                  {[
                    { category: 'Salaries', amount: orgCosts.salaries, color: 'text-blue-400', icon: '💰' },
                    { category: 'Benefits', amount: orgCosts.benefits, color: 'text-green-400', icon: '🏥' },
                    { category: 'Training', amount: orgCosts.training, color: 'text-purple-400', icon: '📚' },
                    { category: 'Recruiting', amount: orgCosts.recruiting, color: 'text-orange-400', icon: '🎯' },
                    { category: 'External Audits', amount: orgCosts.externalAudit, color: 'text-red-400', icon: '🔍' },
                    { category: 'Consulting', amount: orgCosts.consulting, color: 'text-cyan-400', icon: '💼' },
                    { category: 'Software', amount: orgCosts.software, color: 'text-pink-400', icon: '💻' },
                    { category: 'Office Space', amount: orgCosts.officeSpace, color: 'text-yellow-400', icon: '🏢' }
                  ].map((cost, index) => (
                    <div key={index} className="flex justify-between items-center p-2 hover:bg-slate-800 rounded transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{cost.icon}</span>
                        <div>
                          <div className="text-white font-medium">{cost.category}</div>
                          <div className="text-xs text-slate-500">
                            {cost.category === 'Benefits' && `(${Math.round((orgCosts.benefits / orgCosts.salaries) * 100)}% of salaries)`}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${cost.color}`}>
                          {formatCurrency(cost.amount)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {cost.amount === 0 ? 'Not Available' : 'Annually'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-700 pt-4 mt-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-lg font-bold text-white">Total Annual Cost</span>
                    <span className="text-2xl font-bold text-green-400">{formatCurrency(orgCosts.total)}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-lg font-bold text-white">Monthly Burn Rate</span>
                    <span className="text-xl font-bold text-blue-400">{formatCurrency(orgCosts.total / 12)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Optimization */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">Cost Optimization</h4>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-lg font-bold text-yellow-400">
                      {((orgCosts.benefits + orgCosts.training + orgCosts.recruiting) / orgCosts.salaries * 100).toFixed(1)}%
                    </div>
                    <div className="text-sm text-slate-400">Overhead Rate</div>
                    <div className="text-xs text-yellow-400 mt-1">
                      {(orgCosts.benefits + orgCosts.training + orgCosts.recruiting) / orgCosts.salaries > 0.3 ? 'Above Optimal' : 'Well Managed'}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-400">
                      {formatCurrency(orgCosts.total / Math.max(1, employees.length))}
                    </div>
                    <div className="text-sm text-slate-400">Cost per Employee</div>
                    <div className="text-xs text-purple-400 mt-1">
                      {orgCosts.total / employees.length <= 150000 ? 'Efficient' : 'Optimize'}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700">
                  <h5 className="text-white font-medium mb-3">Cost Reduction Opportunities</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-green-900/20 border border-green-800/30 rounded">
                      <span className="text-sm text-green-400">Remote Work Savings</span>
                      <span className="text-sm text-green-400">Up to 20% reduction</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-900/20 border border-blue-800/30 rounded">
                      <span className="text-sm text-blue-400">Process Automation</span>
                      <span className="text-sm text-blue-400">15% efficiency gain</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-purple-900/20 border border-purple-800/30 rounded">
                      <span className="text-sm text-purple-400">Vendor Consolidation</span>
                      <span className="text-sm text-purple-400">10% cost reduction</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Planning */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg">
          <h3 className="text-xl font-bold text-white mb-6">Financial Planning & Projections</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">Growth Projections</h4>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-300">Next 6 Months</span>
                      <span className="text-sm font-medium text-white">
                        {formatCurrency((orgCosts?.total || 0) * 0.5)} (50% growth)
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className="h-2 bg-blue-500 rounded-full transition-all duration-500" style={{ width: '50%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-300">Next 12 Months</span>
                      <span className="text-sm font-medium text-white">
                        {formatCurrency((orgCosts?.total || 0) * 1.2)} (20% growth)
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className="h-2 bg-blue-500 rounded-full transition-all duration-500" style={{ width: '60%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-300">Next 24 Months</span>
                      <span className="text-sm font-medium text-white">
                        {formatCurrency((orgCosts?.total || 0) * 1.5)} (50% growth)
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className="h-2 bg-blue-500 rounded-full transition-all duration-500" style={{ width: '75%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white mb-4">ROI Analysis</h4>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-lg font-bold text-cyan-400">
                      {formatCurrency(250000)}
                    </div>
                    <div className="text-sm text-slate-400">Revenue per Employee</div>
                    <div className="text-xs text-cyan-400 mt-1">
                      Industry Average
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-green-400">
                      {formatCurrency(250000 - (orgCosts.total / Math.max(1, employees.length)))}
                    </div>
                    <div className="text-sm text-slate-400">Profit per Employee</div>
                    <div className="text-xs text-green-400 mt-1">
                      {orgCosts.total / Math.max(1, employees.length) <= 150000 ? 'Healthy Margin' : 'Optimize Required'}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700">
                  <h5 className="text-white font-medium mb-3">Investment Priorities</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-emerald-900/20 border border-emerald-800/30 rounded">
                      <span className="text-sm text-emerald-400">High ROI</span>
                      <span className="text-sm text-emerald-400">Sales & Product Teams</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-900/20 border border-blue-800/30 rounded">
                      <span className="text-sm text-blue-400">Medium ROI</span>
                      <span className="text-sm text-blue-400">Engineering & Operations</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-purple-900/20 border border-purple-800/30 rounded">
                      <span className="text-sm text-purple-400">Strategic ROI</span>
                      <span className="text-sm text-purple-400">Compliance & Legal</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Employee Details Modal
  const renderEmployeeDetailsModal = () => {
    if (!selectedEmployee) return null;

    const config = EMPLOYEE_CONFIG[selectedEmployee.type];

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60 p-4">
        <div className="bg-slate-900 rounded-xl border border-slate-600 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-white">Employee Details</h3>
            <button
              onClick={() => setSelectedEmployee(null)}
              className="text-slate-400 hover:text-white text-xl"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            {/* Basic Information */}
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-600">
              <h4 className="text-lg font-semibold text-white mb-4">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-400">Name</div>
                  <div className="text-white font-medium">{selectedEmployee.name}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Role</div>
                  <div className="text-white font-medium">{config.name}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Department</div>
                  <div className="text-white font-medium">{selectedEmployee.department}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Experience</div>
                  <div className="text-white font-medium">{selectedEmployee.experience} years</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Level</div>
                  <div className="text-white font-medium">{config.level}</div>
                </div>
              </div>
            </div>

            {/* Performance & Skills */}
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-600">
              <h4 className="text-lg font-semibold text-white mb-4">Performance & Skills</h4>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-slate-400 mb-2">Productivity Score</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-700 rounded-full h-3">
                      <div
                        className="h-3 rounded-full bg-gradient-to-r from-red-500 to-yellow-500 to-green-500"
                        style={{ width: `${(selectedEmployee.productivity / 1.5) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-white font-medium">{Math.round(selectedEmployee.productivity * 100)}%</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-400 mb-2">Morale Level</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-700 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${
                          selectedEmployee.morale >= 80 ? 'bg-green-500' :
                          selectedEmployee.morale >= 60 ? 'bg-blue-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${selectedEmployee.morale}%` }}
                      ></div>
                    </div>
                    <span className="text-white font-medium">{selectedEmployee.morale}%</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-400 mb-2">Training Level</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-700 rounded-full h-3">
                      <div className="h-3 bg-gradient-to-r from-slate-600 to-purple-500 rounded-full" style={{ width: `${(selectedEmployee.trainingLevel / 5) * 100}%` }}></div>
                    </div>
                    <span className="text-white font-medium">Level {selectedEmployee.trainingLevel}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-700">
                <h5 className="text-white font-medium mb-2">Key Skills</h5>
                <div className="flex flex-wrap gap-2">
                  {config.requiredSkills.map(skill => (
                    <span key={skill} className="px-3 py-1 bg-blue-900/30 text-blue-300 rounded border border-blue-700/50">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-700">
                <h5 className="text-white font-medium mb-2">Certifications</h5>
                <div className="flex flex-wrap gap-2">
                  {selectedEmployee.certifications.map(cert => (
                    <span key={cert} className="px-3 py-1 bg-purple-900/30 text-purple-300 rounded border border-purple-700/50">
                      {cert}
                    </span>
                  ))}
                  {selectedEmployee.certifications.length === 0 && (
                    <span className="text-slate-500 italic">No certifications yet</span>
                  )}
                </div>
              </div>
            </div>

            {/* Compensation */}
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-600">
              <h4 className="text-lg font-semibold text-white mb-4">Compensation Package</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-400">Base Salary</div>
                  <div className="text-lg font-bold text-green-400">{formatCurrency(selectedEmployee.salary)}</div>
                  <div className="text-xs text-slate-500">{formatCurrency(selectedEmployee.salary * 0.08)} / month</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Total Compensation</div>
                  <div className="text-lg font-bold text-blue-400">{formatCurrency(selectedEmployee.totalCompensation)}</div>
                  <div className="text-xs text-slate-500">incl. bonus & equity</div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="text-sm text-slate-400 mb-2">Equity & Bonus Breakdown</div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Annual Bonus</span>
                    <span className="text-white font-medium">{formatCurrency(selectedEmployee.salary * 0.4)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">Equity Value</span>
                    <span className="text-white font-medium">{formatCurrency(selectedEmployee.totalCompensation - selectedEmployee.salary - (selectedEmployee.salary * 0.4))}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Impact */}
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-600">
              <h4 className="text-lg font-semibold text-white mb-4">Business Impact</h4>
              <div className="space-y-2">
                {config.responsibilities.map((resp, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <div className="text-sm text-slate-300">{resp}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-700">
                <h5 className="text-white font-medium mb-2">Key Metrics</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-300">Hire Date</span>
                    <span className="text-white font-medium">
                      {new Date(selectedEmployee.hireDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-300">Manager</span>
                    <span className="text-white font-medium">
                      {selectedEmployee.reportsTo ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setSelectedEmployee(null)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  // In a real implementation, this would open an edit form
                  showNotification('Employee edit functionality coming soon', 'info');
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to terminate this employee?')) {
                    handleTerminateEmployee(selectedEmployee.id);
                    setSelectedEmployee(null);
                  }
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-colors"
              >
                Terminate
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <EnterpriseDashboardShell
        title="HR & Organizational Management"
        subtitle="Workforce planning, hiring pipeline, and organizational analytics"
        icon="👥"
        onClose={onClose}
        testId="hr-dashboard-enhanced"
        tabs={
          <div className="flex border-b border-slate-700 overflow-x-auto shrink-0">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'headcount', label: 'Headcount', icon: '👥' },
              { id: 'hiring', label: 'Hiring', icon: '🎯' },
              { id: 'metrics', label: 'Analytics', icon: '📈' },
              { id: 'costs', label: 'Cost Analysis', icon: '💰' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-6 py-3 text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-900/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        }
      >
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'headcount' && renderHeadcount()}
        {activeTab === 'hiring' && renderHiring()}
        {activeTab === 'metrics' && renderMetrics()}
        {activeTab === 'costs' && renderCosts()}
      </EnterpriseDashboardShell>

      {renderEmployeeDetailsModal()}
    </>
  );
};

export default HRDashboardEnhanced;